import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TrendingSidebar from '../components/TrendingSidebar';
import VisitorCounter from '../components/VisitorCounter';
import ContentTeaser from '../components/ContentTeaser';
import LivePricesTicker from '../components/LivePricesTicker';
import LivePricesWidget from '../components/LivePricesWidget';
import ExnessBrokerAd from '../components/ExnessBrokerAd';
import PropResultsSection from '../components/PropResultsSection';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// YouTube Videos Component
function YouTubeSection() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos?active_only=true`);
      setVideos(response.data.slice(0, 3)); // Only show first 3
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || videos.length === 0) return null;

  return (
    <section className="section-mobile py-16 sm:py-24 px-4 sm:px-6 max-w-7xl mx-auto border-t border-zinc-800">
      <div className="mb-8 sm:mb-12">
        <h2 className="section-heading text-3xl sm:text-5xl md:text-6xl font-extralight tracking-tight mb-3 sm:mb-4">
          LATEST VIDEOS
        </h2>
        <p className="text-gray-400 font-light text-base sm:text-lg">Watch John Ghatti's insights on trading and AI</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        {videos.map((video) => (
          <div 
            key={video.id} 
            className="group bg-zinc-900/50 backdrop-blur-md border border-zinc-800 hover:border-[#00FF7F]/30 transition-all duration-500 overflow-hidden rounded-xl"
          >
            {/* YouTube Embed */}
            <div className="relative pb-[56.25%] bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${video.video_id}`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            {/* Video Info */}
            <div className="p-4 sm:p-6">
              <h3 className="text-base sm:text-xl font-light mb-2 group-hover:text-gray-300 transition-colors line-clamp-2">
                {video.title}
              </h3>
              {video.description && (
                <p className="text-gray-400 font-extralight text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2">
                  {video.description}
                </p>
              )}
              <a 
                href={video.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold tracking-widest group-hover:gap-3 transition-all duration-300 border-b border-white pb-1 hover:border-[#00FF7F] hover:text-[#00FF7F]"
              >
                WATCH ON YOUTUBE
                <span>→</span>
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* YouTube Channel Link */}
      <div className="mt-8 sm:mt-12 text-center">
        <a 
          href="https://youtube.com/@johnghattiworldwide" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-3 sm:py-4 border-2 border-white hover:bg-white hover:text-black transition-all duration-300 text-xs sm:text-sm font-medium tracking-widest w-full sm:w-auto rounded-lg"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
          SUBSCRIBE ON YOUTUBE
        </a>
      </div>
    </section>
  );
}

// Featured Articles Component  
function FeaturedArticlesSection({ articles }) {
  // Check if articles exist and have data
  if (!articles || articles.length === 0) {
    return (
      <section id="articles" className="py-16 px-6 max-w-6xl mx-auto">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-3" data-testid="articles-heading">
            KASANGO JOURNAL
          </h2>
          <div className="h-0.5 w-20 bg-[#00FF7F] mb-6"></div>
          <p className="text-gray-400 font-extralight">
            No articles available yet. Visit the admin panel to create and feature articles.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="articles" className="py-16 px-6 max-w-6xl mx-auto">
      <div className="mb-12">
        <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-3" data-testid="articles-heading">
          KASANGO JOURNAL
        </h2>
        <div className="h-0.5 w-20 bg-[#00FF7F] mb-6"></div>
        
        {/* Category Tags */}
        <div className="flex flex-wrap gap-3 mb-4">
          <span className="px-4 py-2 bg-zinc-900 border border-[#00FF7F]/30 text-[#00FF7F] text-sm font-light tracking-wide hover:bg-[#00FF7F]/10 transition-colors cursor-pointer">
            Results
          </span>
          <span className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-gray-300 text-sm font-extralight tracking-wide hover:border-zinc-500 transition-colors cursor-pointer">
            Trading Education
          </span>
          <span className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-gray-300 text-sm font-extralight tracking-wide hover:border-zinc-500 transition-colors cursor-pointer">
            AI Research
          </span>
          <span className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-gray-300 text-sm font-extralight tracking-wide hover:border-zinc-500 transition-colors cursor-pointer">
            Ghatti News
          </span>
        </div>
        
        <p className="text-gray-400 font-extralight">Insights on Forex AI, trading strategies, and market analysis</p>
      </div>

      {/* Hero Article - Large Featured */}
      <article 
        className="group relative bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 overflow-hidden mb-8"
        data-testid={`article-${articles[0].id}`}
      >
        <div className="grid lg:grid-cols-5 gap-0">
          {/* Featured Image - Left (3 columns) */}
          <div className="lg:col-span-3 relative h-96 lg:h-[500px] overflow-hidden">
            <img 
              src={articles[0].featured_image} 
              alt={articles[0].title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-zinc-950/90 lg:to-zinc-950"></div>
            
            {/* Category Badge */}
            <div className="absolute top-6 left-6">
              <span className="px-4 py-1.5 bg-white text-black text-xs font-bold tracking-wider">
                {articles[0].category.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Content - Right (2 columns) */}
          <div className="lg:col-span-2 p-8 lg:p-10 flex flex-col justify-center bg-zinc-950">
            {/* Meta Info */}
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full border border-zinc-700 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                    alt="John Ghatti"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-gray-400 font-medium text-xs">John Ghatti</span>
              </div>
              <span>•</span>
              <span>{articles[0].readTime}</span>
            </div>

            {/* Title */}
            <h3 className="text-3xl lg:text-4xl font-light mb-5 group-hover:text-gray-300 transition-colors leading-tight">
              {articles[0].title}
            </h3>

            {/* Summary */}
            <p className="text-gray-400 font-light leading-relaxed mb-6 text-sm">
              {articles[0].summary}
            </p>

            {/* Read More Link */}
            <a 
              href={`/article/${articles[0].id}`} 
              className="inline-flex items-center gap-2 text-xs font-bold tracking-widest group-hover:gap-3 transition-all duration-300 border-b border-white pb-1 w-fit" 
              data-testid={`read-article-${articles[0].id}`}
            >
              READ MORE
              <span>→</span>
            </a>
          </div>
        </div>
      </article>

      {/* Remaining Articles - Magazine Grid (2 + 2 layout) */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {articles.slice(1, 3).map((a, index) => (
          <article 
            key={a.id} 
            className="group relative bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 overflow-hidden flex flex-col"
            data-testid={`article-${a.id}`}
          >
            {/* Featured Image */}
            <div className="relative h-48 overflow-hidden">
              <img 
                src={a.featured_image} 
                alt={a.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent"></div>
              
              {/* Category Badge */}
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-white text-black text-xs font-bold tracking-wider">
                  {a.category.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 flex flex-col">
              {/* Meta Info */}
              <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                <span className="text-gray-400">John Ghatti</span>
                <span>•</span>
                <span>{a.readTime}</span>
              </div>

              {/* Title */}
              <h4 className="text-xl font-light mb-3 group-hover:text-gray-300 transition-colors leading-tight flex-1">
                {a.title}
              </h4>

              {/* Summary */}
              <p className="text-gray-400 font-light text-sm leading-relaxed mb-4 line-clamp-3">
                {a.summary}
              </p>

              {/* Read More Link */}
              <a 
                href={`/article/${a.id}`} 
                className="inline-flex items-center gap-2 text-xs font-bold tracking-widest group-hover:gap-3 transition-all duration-300 border-b border-white pb-1 w-fit" 
                data-testid={`read-article-${a.id}`}
              >
                READ MORE
                <span>→</span>
              </a>
            </div>
          </article>
        ))}
      </div>

      {/* Secondary Grid - 3 column layout for remaining */}
      <div className="grid md:grid-cols-3 gap-6">
        {articles.slice(3).map((a, index) => (
          <article 
            key={a.id} 
            className="group relative bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 overflow-hidden"
            data-testid={`article-${a.id}`}
          >
            {/* Featured Image */}
            <div className="relative h-40 overflow-hidden">
              <img 
                src={a.featured_image} 
                alt={a.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/10 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Category */}
              <span className="inline-block px-2 py-0.5 bg-zinc-800 text-white text-xs font-bold tracking-wider mb-3">
                {a.category.toUpperCase()}
              </span>

              {/* Title */}
              <h4 className="text-base font-light mb-2 group-hover:text-gray-300 transition-colors leading-tight line-clamp-2">
                {a.title}
              </h4>

              {/* Meta Info */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <span>{a.readTime}</span>
              </div>

              {/* Read More Link */}
              <a 
                href={`/article/${a.id}`} 
                className="inline-flex items-center gap-2 text-xs font-bold tracking-widest group-hover:gap-3 transition-all duration-300" 
                data-testid={`read-article-${a.id}`}
              >
                READ
                <span>→</span>
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// Trending Articles Section - Below Kasango Journal
function TrendingArticlesSection() {
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=6`);
      setTrendingArticles(response.data);
    } catch (err) {
      console.error('Error fetching trending articles:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || trendingArticles.length === 0) return null;

  return (
    <section className="py-16 px-6 max-w-6xl mx-auto border-t border-zinc-800">
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#00FF7F] animate-pulse"></div>
          <h2 className="text-3xl md:text-4xl font-light tracking-tight">
            TRENDING NOW
          </h2>
        </div>
        <div className="h-0.5 w-20 bg-[#00FF7F] mb-6"></div>
        <p className="text-gray-400 font-extralight">Most viewed articles this week</p>
      </div>

      {/* Trending Articles Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {trendingArticles.map((article, index) => (
          <Link
            key={article.id}
            to={`/article/${article.id}`}
            className="group relative bg-zinc-950 border border-zinc-800 hover:border-[#00FF7F]/50 transition-all duration-300 overflow-hidden rounded-lg"
          >
            {/* Trending Number Badge */}
            <div className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-[#00FF7F] text-black flex items-center justify-center font-bold text-lg">
              {index + 1}
            </div>

            {/* Featured Image */}
            <div className="relative h-48 overflow-hidden">
              <img 
                src={article.featured_image || 'https://via.placeholder.com/400x300?text=Article'} 
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Category */}
              {article.category && (
                <span className="inline-block px-2 py-0.5 bg-[#00FF7F]/20 text-[#00FF7F] text-xs font-bold tracking-wider mb-3 border border-[#00FF7F]/30">
                  {article.category.toUpperCase()}
                </span>
              )}

              {/* Title */}
              <h4 className="text-lg font-light mb-3 group-hover:text-[#00FF7F] transition-colors leading-tight line-clamp-2">
                {article.title}
              </h4>

              {/* Meta Info */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>{article.view_count || 0} views</span>
                </div>
                <span className="text-gray-600">→</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

// Trending Headlines Ticker - Sliding Left
function TrendingTopBar() {
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=8`);
      setTrendingArticles(response.data);
    } catch (err) {
      console.error('Error fetching trending articles:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || trendingArticles.length === 0) return null;

  return (
    <section className="bg-zinc-950 border-y border-zinc-900 py-3 overflow-hidden">
      <div className="flex items-center">
        {/* Fixed Label */}
        <div className="flex-shrink-0 bg-[#00FF7F] px-6 py-3 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-black animate-pulse"></div>
          <span className="text-xs font-light tracking-widest text-black uppercase">
            Trending Now
          </span>
        </div>
        
        {/* Vertical Divider */}
        <div className="w-px h-8 bg-zinc-800 mx-4"></div>
        
        {/* Scrolling Headlines */}
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-12 animate-scroll-left">
            {/* Duplicate articles for seamless loop */}
            {trendingArticles.concat(trendingArticles).map((article, index) => (
              <Link
                key={`${article.id}-${index}`}
                to={`/article/${article.id}`}
                className="flex-shrink-0 group flex items-center gap-4"
              >
                <span className="text-sm font-extralight text-zinc-700 group-hover:text-[#00FF7F] transition-colors">
                  {((index % trendingArticles.length) + 1).toString().padStart(2, '0')}
                </span>
                <span className="text-sm font-light text-gray-300 group-hover:text-white transition-colors whitespace-nowrap">
                  {article.title}
                </span>
                <div className="w-1.5 h-1.5 rounded-full bg-zinc-800"></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Mobile Trending Section Component
function MobileTrendingSection() {
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending();
  }, []);

  const fetchTrending = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=5`);
      setTrendingArticles(response.data);
    } catch (err) {
      console.error('Error fetching trending articles:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || trendingArticles.length === 0) return null;

  return (
    <section className="lg:hidden px-4 py-6 border-b border-zinc-900 bg-black">
      <div className="mb-5">
        <h2 className="text-xs font-light tracking-widest text-gray-500 uppercase">
          Trending Now
        </h2>
      </div>
      
      {/* Horizontal Scroll on Mobile - Forbes Style */}
      <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
        {trendingArticles.map((article, index) => (
          <Link
            key={article.id}
            to={`/article/${article.id}`}
            className="flex-shrink-0 w-72 snap-start bg-zinc-950 border border-zinc-900 hover:border-zinc-800 transition-colors p-5"
          >
            <div className="flex items-start gap-4">
              <span className="text-2xl font-extralight text-zinc-800 leading-none">
                {(index + 1).toString().padStart(2, '0')}
              </span>
              <h3 className="text-sm font-light leading-snug text-gray-300 line-clamp-3 flex-1 pt-1">
                {article.title}
              </h3>
            </div>
          </Link>
        ))}
      </div>
      
      {/* Scroll Indicator */}
      <div className="flex items-center justify-center gap-1.5 mt-4">
        {trendingArticles.map((_, index) => (
          <div key={index} className="w-1 h-1 rounded-full bg-zinc-800"></div>
        ))}
      </div>
    </section>
  );
}

function JohnHomepage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [mobileZoom, setMobileZoom] = useState(1); // 1 = normal, 0.85 = zoomed out
  const [isLandscape, setIsLandscape] = useState(true);
  const [showOrientationPrompt, setShowOrientationPrompt] = useState(false);
  const [showChatTeaser, setShowChatTeaser] = useState(false);
  const [chatTeaserMinimized, setChatTeaserMinimized] = useState(false);
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [articles, setArticles] = useState([]);
  const { addToCart, getCartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch featured articles for Kasango Journal
  useEffect(() => {
    const fetchFeaturedArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles/featured`);
        setArticles(response.data);
      } catch (err) {
        console.error('Error fetching featured articles:', err);
      }
    };
    fetchFeaturedArticles();
  }, []);

  // Mobile Zoom Effect
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      document.body.style.zoom = mobileZoom;
      // Alternative for browsers that don't support zoom
      document.body.style.transform = `scale(${mobileZoom})`;
      document.body.style.transformOrigin = 'top center';
      
      if (mobileZoom !== 1) {
        document.body.style.width = `${100 / mobileZoom}%`;
      } else {
        document.body.style.width = '100%';
      }
    }
    
    return () => {
      document.body.style.zoom = '';
      document.body.style.transform = '';
      document.body.style.width = '';
    };
  }, [mobileZoom]);

  // Orientation Detection for Mobile - DISABLED for better Google Ads performance
  // Users can now view the site immediately in any orientation
  useEffect(() => {
    const checkOrientation = () => {
      const isMobileDevice = window.innerWidth < 768;
      const isCurrentlyLandscape = window.innerWidth > window.innerHeight;
      
      if (isMobileDevice) {
        setIsLandscape(isCurrentlyLandscape);
        // Orientation prompt disabled - no blocking overlay
        setShowOrientationPrompt(false);
      } else {
        setShowOrientationPrompt(false);
      }
    };

    // Check on mount
    checkOrientation();

    // Listen for orientation changes
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Chat Teaser - Show after 5-8 seconds, minimize after 10 seconds
  useEffect(() => {
    // Random delay between 5-8 seconds
    const showDelay = Math.random() * 3000 + 5000; // 5000-8000ms
    
    const showTimer = setTimeout(() => {
      setShowChatTeaser(true);
      
      // Play message pop sound (optional - using browser's default)
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYHHGi77OihURALUKrj77RiHAU7k9r1zXsuBSh+zPLaizsIGGS57OmkURAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU2jdXzzn0vBSl+zPPbiTwIF2S56+ykUBAKTKXh8bllHgU=');
        audio.volume = 0.3;
        audio.play().catch(() => {}); // Ignore if autoplay blocked
      } catch (e) {
        console.log('Audio playback not supported');
      }
      
      // Minimize after 10 seconds
      setTimeout(() => {
        setChatTeaserMinimized(true);
      }, 10000);
    }, showDelay);

    return () => clearTimeout(showTimer);
  }, []);

  // Re-show chat teaser on scroll to top
  useEffect(() => {
    const handleScroll = () => {
      if (chatTeaserMinimized && window.scrollY < 100) {
        setChatTeaserMinimized(false);
        // Re-minimize after 10 seconds
        setTimeout(() => {
          setChatTeaserMinimized(true);
        }, 10000);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [chatTeaserMinimized]);

  useEffect(() => {
    fetchTrendingArticles();
  }, []);

  const fetchTrendingArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles/trending/list?limit=8`);
      setTrendingArticles(response.data);
    } catch (err) {
      console.error('Error fetching trending articles:', err);
    }
  };
  
  const products = {
    kasango: {
      id: 'kasango-pro',
      name: 'Kasango AI Pro',
      description: 'Advanced AI-powered forex trading software - Powered by Kolt AI Engine',
      price: 1450,
      whopLink: 'https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true'
    },
    cashbet: {
      id: 'cashbet-ai',
      name: 'CashBet AI',
      description: 'Next-generation Aviator predictor',
      price: 199,
      whopLink: 'https://whop.com/cashbet-ai/'
    }
  };
  
  const posts = [
    {
      id: 1,
      title: "The Future of Automated Trading in Africa",
      excerpt: "How Kasango 1.0 is democratizing access to AI-powered forex trading and empowering everyday traders across Africa and Asia.",
      date: "2025-10-23",
    },
    {
      id: 2,
      title: "Building Trust in the AI Trading Industry",
      excerpt: "Overcoming skepticism and establishing credibility through transparency, education, and community-first approach.",
      date: "2025-10-22",
    },
    {
      id: 3,
      title: "From Manual Trading to AI Revolution",
      excerpt: "My journey from frustrated manual trader to building Africa's most trusted AI trading robot and franchise system.",
      date: "2025-10-21",
    },
    {
      id: 4,
      title: "Expanding Kasango: 30+ Countries in 3 Years",
      excerpt: "The vision to build Africa's largest AI-driven trading community with licensed partners across the globe.",
      date: "2025-10-20",
    },
    {
      id: 5,
      title: "Mentorship and Financial Literacy",
      excerpt: "Creating jobs, empowering young entrepreneurs, and building financial literacy across underserved communities.",
      date: "2025-10-19",
    },
  ];

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log("Subscribe email:", email);
    alert(`Thanks for subscribing with ${email}!`);
    setEmail("");
  };

  return (
    <div className="min-h-screen bg-black text-white">

      {/* ORIENTATION PROMPT FOR MOBILE - Encourage Landscape */}
      {showOrientationPrompt && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-xl">
          <div className="text-center px-8 py-12 max-w-md">
            {/* Rotate Icon Animation */}
            <div className="mb-8 flex justify-center">
              <div className="relative">
                <svg className="w-24 h-24 text-[#00FF7F] animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <svg className="w-12 h-12 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin-slow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>

            {/* Message */}
            <h2 className="text-2xl font-light text-white mb-4">
              Best Viewed in Landscape
            </h2>
            <p className="text-gray-400 font-extralight text-base mb-2">
              Please rotate your device horizontally for the optimal experience
            </p>
            <p className="text-[#00FF7F] font-light text-sm mb-2">
              Rotate your phone →
            </p>
            <p className="text-gray-500 font-light text-xs italic">
              Auto-continuing in 5 seconds...
            </p>

            {/* Skip Button */}
            <button
              onClick={() => {
                setShowOrientationPrompt(false);
                sessionStorage.setItem('orientation-prompt-dismissed', 'true');
              }}
              className="mt-8 px-6 py-3 border border-zinc-700 hover:border-[#00FF7F] text-sm font-light rounded-xl transition-colors"
            >
              Continue in Portrait
            </button>
          </div>
        </div>
      )}

      {/* INVESTOR MODAL */}
      {showInvestorModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-2xl bg-white border border-zinc-300 rounded-2xl p-8 shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => setShowInvestorModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors text-black"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Content */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-[#00FF7F]/10 border border-[#00FF7F]/30 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-[#00FF7F]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c5.51 0 10-4.48 10-10S17.51 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-black">You've seen the numbers. Now watch it live.</h3>
              <p className="text-gray-700 font-normal mb-6">
                Follow @JohnGhatti on Instagram for verified AI profits, investor logins, and real-time market action.
              </p>
              <p className="text-sm text-[#00DD6F] font-semibold mb-6">
                🔗 Tap "Follow" — Don't just read the results, see them daily.
              </p>
            </div>

            {/* Trading Pairs */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Kasango AI Pro Trades</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-black">
                  <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
                  <span className="font-medium">XAUUSD (Gold)</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-black">
                  <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
                  <span className="font-medium">NAS100</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-black">
                  <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
                  <span className="font-medium">GER30</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-black">
                  <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
                  <span className="font-medium">S&P 500</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300">
                <span className="text-xs text-gray-600 font-normal">Worldwide Tool • 24/7 Automated Trading</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowInvestorModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 hover:border-gray-400 rounded-xl font-medium text-sm transition-colors text-black"
              >
                ← Back
              </button>
              <a
                href="https://www.instagram.com/john_ghatti__ceo_?igsh=MWM2NmYxdXV0a25naw%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl font-light text-sm transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Follow on Instagram
              </a>
              <Link
                to="/investor-proof"
                onClick={() => setShowInvestorModal(false)}
                className="flex-1 px-6 py-3 bg-[#00FF7F] text-black hover:bg-[#00DD6F] rounded-xl font-light text-sm transition-colors text-center"
              >
                Continue to Proof →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT MODAL */}
      {showContactModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl p-8 shadow-2xl">
            {/* Close Button */}
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-zinc-900 rounded-full transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-light mb-2">Get in Touch</h3>
              <p className="text-gray-400 font-extralight text-sm">
                Connect with John Ghatti on your preferred platform
              </p>
            </div>

            {/* Social Links */}
            <div className="space-y-3">
              {/* WhatsApp */}
              <a
                href="https://wa.me/27685358462"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowContactModal(false)}
                className="flex items-center gap-4 w-full px-6 py-4 bg-green-600 hover:bg-green-700 rounded-xl transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-light text-white text-sm">WhatsApp</div>
                  <div className="text-xs text-white/70 font-extralight">Message directly</div>
                </div>
                <svg className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/john_ghatti__ceo_?igsh=MWM2NmYxdXV0a25naw%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowContactModal(false)}
                className="flex items-center gap-4 w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-light text-white text-sm">Instagram</div>
                  <div className="text-xs text-white/70 font-extralight">Follow for live results</div>
                </div>
                <svg className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              {/* Telegram */}
              <a
                href="https://t.me/winnersgroundPwR1"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowContactModal(false)}
                className="flex items-center gap-4 w-full px-6 py-4 bg-blue-500 hover:bg-blue-600 rounded-xl transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-light text-white text-sm">Telegram</div>
                  <div className="text-xs text-white/70 font-extralight">Join the community</div>
                </div>
                <svg className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@john.ghatti?_t=8rQljwbeWfT&_r=1"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowContactModal(false)}
                className="flex items-center gap-4 w-full px-6 py-4 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-zinc-600 rounded-xl transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <svg className="w-6 h-6" fill="white" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="font-light text-white text-sm">TikTok</div>
                  <div className="text-xs text-white/70 font-extralight">Watch trading insights</div>
                </div>
                <svg className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* VISITOR COUNTER WIDGET - HIDDEN */}
      {/* <VisitorCounter /> */}

      {/* TRENDING NOW STRIP - Top of Page */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-zinc-950 border-b border-zinc-900 overflow-hidden h-10 sm:h-12">
        <div className="flex items-center h-full">
          {/* Fixed Label */}
          <div className="flex-shrink-0 bg-[#00FF7F] h-full px-4 sm:px-6 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-black animate-pulse"></div>
            <span className="text-xs font-light tracking-widest text-black uppercase">
              Trending
            </span>
          </div>
          
          {/* Vertical Divider */}
          <div className="w-px h-6 bg-zinc-800 mx-2 sm:mx-3"></div>
          
          {/* Scrolling Headlines */}
          <div className="flex-1 overflow-hidden h-full flex items-center">
            <div className="flex gap-8 sm:gap-10 animate-scroll-left">
              {trendingArticles.concat(trendingArticles).map((article, index) => (
                <Link
                  key={`top-${article.id}-${index}`}
                  to={`/article/${article.id}`}
                  className="flex-shrink-0 group flex items-center gap-2 sm:gap-3"
                >
                  <span className="text-xs font-extralight text-zinc-700 group-hover:text-[#00FF7F] transition-colors">
                    {((index % trendingArticles.length) + 1).toString().padStart(2, '0')}
                  </span>
                  <span className="text-xs font-light text-gray-400 group-hover:text-white transition-colors whitespace-nowrap">
                    {article.title}
                  </span>
                  <div className="w-1 h-1 rounded-full bg-zinc-800"></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CHAT TEASER - "John Ghatti is Online Now" */}
      {showChatTeaser && (
        <div className={`fixed bottom-32 left-6 z-50 transition-all duration-500 ${
          chatTeaserMinimized ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}>
          {!chatTeaserMinimized ? (
            // Full Teaser Card
            <div className="relative animate-fade-up">
              <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-[#00FF7F] rounded-2xl shadow-2xl shadow-[#00FF7F]/20 p-5 max-w-xs backdrop-blur-xl">
                {/* Close Button */}
                <button
                  onClick={() => setShowChatTeaser(false)}
                  className="absolute top-2 right-2 p-1 hover:bg-zinc-800 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Online Indicator & Avatar */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#00FF7F]">
                      <img
                        src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                        alt="John Ghatti"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Online Status */}
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#00FF7F] rounded-full border-2 border-black animate-pulse"></div>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="text-white font-light text-sm mb-1">
                      John Ghatti is online now
                    </h4>
                    <p className="text-gray-400 font-extralight text-xs">
                      Talk to him directly about Kasango AI ⚡
                    </p>
                  </div>
                </div>

                {/* Chat Now Button */}
                <a
                  href="https://wa.me/27685358462?text=Hi%20John%2C%20I%20want%20to%20talk%20about%20Kasango%20AI!"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-3 bg-[#00FF7F] hover:bg-[#00DD6F] text-black font-medium text-sm rounded-xl transition-all duration-300 text-center shadow-lg shadow-[#00FF7F]/30 hover:shadow-xl hover:scale-105"
                >
                  🟩 Chat Now
                </a>
              </div>
            </div>
          ) : (
            // Minimized Chat Bubble
            <a
              href="https://wa.me/27685358462?text=Hi%20John%2C%20I%20want%20to%20talk%20about%20Kasango%20AI!"
              target="_blank"
              rel="noopener noreferrer"
              className="block animate-fade-up"
            >
              <div className="relative">
                {/* Glowing Ring Animation */}
                <div className="absolute inset-0 rounded-full bg-[#00FF7F] animate-ping opacity-20"></div>
                <div className="absolute inset-0 rounded-full border-2 border-[#00FF7F] animate-pulse"></div>
                
                {/* Chat Bubble */}
                <div className="relative w-14 h-14 bg-[#00FF7F] hover:bg-[#00DD6F] rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110">
                  <svg className="w-7 h-7 text-black" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                    <circle cx="9" cy="10" r="1.5"/>
                    <circle cx="15" cy="10" r="1.5"/>
                  </svg>
                  {/* Online Dot */}
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-black animate-pulse"></div>
                </div>
              </div>
            </a>
          )}
        </div>
      )}

      {/* MOBILE ZOOM CONTROL - Only visible on mobile */}
      <div className="fixed bottom-6 left-6 z-40 md:hidden">
        <button
          onClick={() => setMobileZoom(mobileZoom === 1 ? 0.85 : 1)}
          className="w-14 h-14 backdrop-blur-md bg-zinc-900/90 border-2 border-zinc-700 hover:border-[#00FF7F] rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 group"
          title={mobileZoom === 1 ? "Zoom Out for Better View" : "Reset Zoom"}
          aria-label={mobileZoom === 1 ? "Zoom Out" : "Zoom In"}
        >
          {mobileZoom === 1 ? (
            // Zoom Out Icon
            <svg className="w-6 h-6 text-white group-hover:text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          ) : (
            // Zoom In Icon
            <svg className="w-6 h-6 text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          )}
        </button>
        {/* Zoom Indicator */}
        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#00FF7F] text-black flex items-center justify-center text-xs font-bold">
          {mobileZoom === 1 ? '1x' : '0.85x'}
        </div>
      </div>

      {/* FLOATING WHATSAPP & TELEGRAM BUTTONS */}
      <div className="floating-buttons fixed bottom-6 right-6 z-40 flex flex-col gap-3">
        {/* Game Button - Most Prominent */}
        <Link
          to="/game"
          className="w-16 h-16 bg-green-600 hover:bg-green-700 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110"
          title="Play Free Trading Game"
        >
          <div className="w-6 h-6 border-2 border-white rounded"></div>
        </Link>
        
        <a
          href="https://wa.me/27685358462"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
          title="WhatsApp"
        >
          <svg className="w-8 h-8" fill="white" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
        </a>
        
        <a
          href="https://t.me/winnersgroundPwR1"
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110"
          title="Telegram"
        >
          <svg className="w-8 h-8" fill="white" viewBox="0 0 24 24">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        </a>
      </div>

      {/* FLOATING HEADER */}
      <header className={`fixed top-10 sm:top-12 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          <Link to="/profile" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-white/20">
              <img
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                alt="John Ghatti"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-base sm:text-xl font-light tracking-wide">JOHN GHATTI</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => setShowContactModal(true)} className="text-sm font-light hover:text-gray-300 transition-colors">Contact</button>
            <a href="#about" className="text-sm font-light hover:text-gray-300 transition-colors">About</a>
            <Link to="/investor-proof" className="text-sm font-light hover:text-gray-300 transition-colors">Results</Link>
            <a href="https://app.winnersgroundfx.com" target="_blank" rel="noopener noreferrer" className="text-sm font-light hover:text-gray-300 transition-colors">Kasango AI</a>
          </nav>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/cart"
              className="relative p-2 hover:bg-zinc-800 transition-colors rounded"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Link>
            
            {/* Hamburger Menu Button - Mobile Only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-zinc-800 transition-colors rounded"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
            
            {/* Desktop Buy Buttons */}
            <a
              href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-4 sm:px-5 py-2 text-xs font-medium tracking-wider border border-white/30 hover:bg-white hover:text-black transition-all duration-300"
              data-testid="kasango-link"
            >
              BUY KASANGO AI
            </a>
            <a
              href="https://whop.com/cashbet-ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-4 sm:px-5 py-2 text-xs font-medium tracking-wider bg-white text-black hover:bg-gray-200 transition-all duration-300"
              data-testid="cashbet-link"
            >
              BUY CASHBET AI
            </a>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/98 border-t border-zinc-800 backdrop-blur-lg">
            <nav className="flex flex-col py-4 px-6 space-y-4">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowContactModal(true);
                }}
                className="text-base font-light hover:text-gray-300 transition-colors py-2 text-left"
              >
                Contact
              </button>
              <a 
                href="#about" 
                className="text-base font-light hover:text-gray-300 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </a>
              <Link 
                to="/investor-proof" 
                className="text-base font-light hover:text-gray-300 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Results
              </Link>
              <a 
                href="https://app.winnersgroundfx.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-base font-light hover:text-gray-300 transition-colors py-2"
              >
                Kasango AI
              </a>
              <div className="pt-4 border-t border-zinc-800 space-y-3">
                <a
                  href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center px-5 py-3 text-sm font-medium tracking-wider border border-white/30 hover:bg-white hover:text-black transition-all duration-300"
                  data-testid="kasango-link-mobile"
                >
                  BUY KASANGO AI
                </a>
                <a
                  href="https://whop.com/cashbet-ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center px-5 py-3 text-sm font-medium tracking-wider bg-white text-black hover:bg-gray-200 transition-all duration-300"
                  data-testid="cashbet-link-mobile"
                >
                  BUY CASHBET AI
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* HERO SECTION - Full Screen */}
      <section className="hero-section relative h-screen flex items-center justify-center overflow-hidden pt-40 sm:pt-36 md:pt-40">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto">

          {/* New Headline */}
          <h1 className="hero-headline text-4xl sm:text-6xl md:text-8xl font-bold tracking-tight mb-2 sm:mb-3 animate-fade-in mt-6 sm:mt-8">
            Minister of Forex AI
            <br />
            <span className="font-light text-[#00FF7F]">Kasango AI Pro</span>
          </h1>
          
          {/* Powered By Kolt AI Engine */}
          <div className="mb-4 sm:mb-6">
            <p className="text-lg sm:text-xl text-gray-400 font-light">
              Powered by <span className="text-white font-medium">Kolt AI Engine</span>
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-2 italic">
              Kolt AI is the intelligence engine operating inside Kasango AI Pro.
            </p>
          </div>
          
          {/* Tagline */}
          <p className="hero-tagline text-xl sm:text-2xl md:text-3xl font-light text-white mb-3 sm:mb-4 tracking-wide">
            Smart. Profitable. Verified.
          </p>
          
          {/* Subheadline */}
          <p className="hero-subheadline text-base sm:text-lg md:text-xl font-light text-gray-300 mb-8 sm:mb-10 px-2 max-w-4xl mx-auto">
            Join 10,000+ traders using AI to grow profits with zero emotions and full automation.
          </p>
          
          {/* Primary CTA Buttons */}
          <div className="hero-cta-group flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4 mb-8">
            <a 
              href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group relative w-full sm:w-auto px-10 sm:px-12 py-4 sm:py-5 bg-[#00FF7F] text-black font-light text-sm sm:text-base tracking-widest hover:bg-[#00DD6F] transition-all duration-300 shadow-lg shadow-[#00FF7F]/50 hover:shadow-xl hover:shadow-[#00FF7F]/70 hover:scale-105"
            >
              GET KASANGO AI PRO
            </a>
            {/* Mobile: Navigate to results | Desktop: Show modal */}
            <button 
              onClick={() => {
                if (window.innerWidth < 768) {
                  // Mobile: Navigate directly to results page
                  navigate('/investor-proof');
                } else {
                  // Desktop: Show modal
                  setShowInvestorModal(true);
                }
              }}
              className="group relative w-full sm:w-auto px-10 sm:px-12 py-4 sm:py-5 bg-white text-black font-light text-sm sm:text-base tracking-widest hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105"
            >
              WATCH LIVE RESULTS
            </button>
            <a 
              href="https://wa.me/27685358462?text=Hi%2C%20I%20want%20to%20learn%20more%20about%20Kasango%20AI%20Pro!" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group relative w-full sm:w-auto px-10 sm:px-12 py-4 sm:py-5 bg-zinc-900 border border-white/30 text-white font-light text-sm sm:text-base tracking-widest hover:bg-white hover:text-black transition-all duration-300 shadow-lg hover:scale-105"
            >
              CHAT WITH JOHN GHATTI
            </a>
          </div>

          {/* Demo Backtest Link */}
          <div className="text-center mt-6">
            <Link
              to="/demo-backtest"
              className="inline-flex items-center gap-2 text-[#00FF7F] hover:text-[#00DD6F] font-light text-sm transition-colors group"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Try Free Demo Backtest – See Your Potential Profit
            </Link>
          </div>

          {/* Proof Section - Below Buttons */}
          <div className="hero-proof-grid grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto mt-8 sm:mt-12">
            <div className="flex items-center justify-center gap-3 text-sm sm:text-base text-gray-300">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="font-extralight">User results vary – Past performance: R4.3M+ total volume traded</span>
            </div>
            <div className="flex items-center justify-center gap-3 text-sm sm:text-base text-gray-300">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="font-extralight">Trusted by <span className="font-light text-white">10 countries</span></span>
            </div>
            <div className="flex items-center justify-center gap-3 text-sm sm:text-base text-gray-300">
              <div className="w-2 h-2 rounded-full bg-[#00FF7F]"></div>
              <span className="font-extralight">Zero emotion, <span className="font-light text-white">full AI control</span></span>
            </div>
          </div>

          {/* Tesla-Style Badge Features - Key Differentiators */}
          <div className="tesla-badges-container flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 max-w-5xl mx-auto mt-12 sm:mt-16 pt-8 sm:pt-12 border-t border-zinc-800/50">
            {/* Works Offline Badge */}
            <div className="tesla-badge group flex flex-col items-center gap-3 px-6 py-4 rounded-2xl backdrop-blur-md bg-white/5 border border-white/10 hover:border-[#00FF7F]/50 transition-all duration-500 hover:bg-white/10 w-full sm:min-w-[240px]">
              <div className="w-10 h-10 rounded-full bg-[#00FF7F]/10 border border-[#00FF7F]/30 flex items-center justify-center group-hover:bg-[#00FF7F]/20 transition-colors">
                <svg className="w-5 h-5 text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-center">
                <h4 className="text-base font-light text-white mb-1">Works Offline</h4>
                <p className="text-xs font-extralight text-gray-400">No Internet • No VPS needed</p>
              </div>
            </div>

            {/* 100% Automated Badge */}
            <div className="tesla-badge group flex flex-col items-center gap-3 px-6 py-4 rounded-2xl backdrop-blur-md bg-white/5 border border-white/10 hover:border-[#00FF7F]/50 transition-all duration-500 hover:bg-white/10 w-full sm:min-w-[240px]">
              <div className="w-10 h-10 rounded-full bg-[#00FF7F]/10 border border-[#00FF7F]/30 flex items-center justify-center group-hover:bg-[#00FF7F]/20 transition-colors">
                <svg className="w-5 h-5 text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="text-center">
                <h4 className="text-base font-light text-white mb-1">100% Automated</h4>
                <p className="text-xs font-extralight text-gray-400">Fully hands-free trading</p>
              </div>
            </div>

            {/* Pioneering Innovation Badge */}
            <div className="tesla-badge group flex flex-col items-center gap-3 px-6 py-4 rounded-2xl backdrop-blur-md bg-white/5 border border-white/10 hover:border-[#00FF7F]/50 transition-all duration-500 hover:bg-white/10 w-full sm:min-w-[240px]">
              <div className="w-10 h-10 rounded-full bg-[#00FF7F]/10 border border-[#00FF7F]/30 flex items-center justify-center group-hover:bg-[#00FF7F]/20 transition-colors">
                <svg className="w-5 h-5 text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <div className="text-center">
                <h4 className="text-base font-light text-white mb-1">Pioneering</h4>
                <p className="text-xs font-extralight text-gray-400">Forex AI App</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* LIVE PRICES TICKER */}
      <LivePricesTicker />

      {/* PROP FIRM RESULTS */}
      <PropResultsSection />

      {/* PHASE 2: MEET JOHN GHATTI - AUTHORITY SECTION */}
      <section className="section-mobile py-20 sm:py-32 px-4 sm:px-6 bg-black border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="meet-john-grid grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Portrait */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#00FF7F] to-green-600 opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500"></div>
              <div className="relative overflow-hidden border-4 border-[#00FF7F]/30 hover:border-[#00FF7F]/60 transition-all duration-500">
                <img
                  src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/vio7h01p_image.png"
                  alt="John Ghatti"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-tight mb-6">
                Meet <span className="text-[#00FF7F] font-semibold">John Ghatti</span>
              </h2>
              <p className="text-xl sm:text-2xl text-gray-300 font-light mb-8 leading-relaxed">
                The man behind the Forex AI Revolution.
              </p>
              
              <div className="space-y-4 mb-10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Civil Engineering Graduate — University of Johannesburg
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Founder of Ghatti Trading & Kasango AI
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Founder of AI trading software in Africa — Pioneering automated trading technology across the continent
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    High-Frequency Programmer — Creator of next-gen Forex & Crypto algorithms
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Entrepreneur — Leading multiple AI-driven tech and fintech ventures
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Property Investor — Building high-end modern architecture across Africa
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Philanthropist — Empowering African youth through AI education & innovation
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#00FF7F] mt-2"></div>
                  <p className="text-base sm:text-lg text-gray-300 font-extralight">
                    Featured on Forbes Africa — Recognized for transforming the Forex landscape
                  </p>
                </div>
              </div>

              <a
                href="https://youtu.be/uyjisJDN1Pw?si=6tJG9AISmIsyDv_w"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#00FF7F] text-black font-bold text-base hover:bg-[#00DD6F] transition-all duration-300 shadow-lg shadow-[#00FF7F]/30 hover:shadow-xl hover:scale-105"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                Watch Interview
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRENDING NOW - TOP STORY BAR */}
      <TrendingTopBar />

      {/* PHASE 3: FEATURED PRODUCTS HUB - Tesla Glassmorphism */}
      <section className="section-mobile py-20 sm:py-32 px-4 sm:px-6 bg-black border-t border-zinc-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="section-heading text-4xl sm:text-5xl md:text-6xl font-extralight tracking-tight mb-4">
              Our <span className="text-[#00FF7F] font-light">AI Products</span>
            </h2>
            <p className="section-subheading text-xl text-gray-400 font-extralight max-w-3xl mx-auto">
              Advanced AI-powered solutions for traders and investors worldwide
            </p>
          </div>

          <div className="product-grid grid md:grid-cols-3 gap-8">
            {/* Kasango AI Pro Card - Tesla Glass */}
            <div className="product-card group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#00FF7F]/50 transition-all duration-700 p-8 hover:shadow-2xl hover:shadow-[#00FF7F]/10 hover:-translate-y-2">
              {/* Animated Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#00FF7F]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              {/* Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full backdrop-blur-md bg-[#00FF7F]/20 border border-[#00FF7F]/50 text-[#00FF7F] text-xs font-light tracking-wider">
                BEST SELLER
              </div>
              
              <div className="relative z-10 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#00FF7F]/10 border border-[#00FF7F]/30 flex items-center justify-center mb-6">
                  <div className="w-6 h-6 rounded bg-[#00FF7F]"></div>
                </div>
                <h3 className="text-2xl font-light text-white mb-2 tracking-wide">Kasango AI Pro</h3>
                <p className="text-sm text-gray-500 mb-4 italic">Powered by Kolt AI Engine</p>
                <p className="text-gray-400 font-extralight leading-relaxed mb-6 text-sm">
                  Advanced AI trading software for market analysis and automated execution. Designed for NAS100, Gold, and GBPJPY using rule-based algorithms.
                </p>
                
                {/* Key Capabilities */}
                <div className="space-y-3 mb-6 p-4 rounded-xl bg-[#00FF7F]/5 border border-[#00FF7F]/20">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00FF7F]"></div>
                    <span className="font-light text-[#00FF7F] text-sm">Works without VPS (standard internet connection required)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00FF7F]"></div>
                    <span className="font-light text-[#00FF7F] text-sm">Automated trade execution based on predefined algorithms</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00FF7F]"></div>
                    <span className="font-light text-[#00FF7F] text-sm">AI-assisted market analysis and signal generation</span>
                  </div>
                </div>
                
                <div className="space-y-3 mb-4 text-sm text-gray-300">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-gray-500"></div>
                    <span className="font-extralight">Multi-session market operation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-gray-500"></div>
                    <span className="font-extralight">User dashboard with trade visibility</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-gray-500"></div>
                    <span className="font-extralight">Risk-controlled execution based on user settings</span>
                  </div>
                </div>
                
                {/* Legal Disclaimer */}
                <div className="mb-6 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <p className="text-[10px] text-gray-500 leading-relaxed">
                    ⚠️ <span className="font-semibold">Important:</span> Trading involves risk and may result in financial loss. Kasango AI Pro is a software tool, not a financial service. No profit guarantees or win-rate assurances are made. Past or illustrative performance does not guarantee future results.
                  </p>
                </div>
                
                <div className="text-center mb-4">
                  <span className="inline-block px-3 py-1 text-[10px] text-gray-400 bg-zinc-900/50 rounded-full border border-zinc-800">
                    Software Tool • No Guaranteed Returns
                  </span>
                </div>
              </div>
              <a
                href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
                target="_blank"
                rel="noopener noreferrer"
                className="relative z-10 block w-full text-center px-6 py-4 rounded-xl backdrop-blur-md bg-[#00FF7F]/90 hover:bg-[#00FF7F] text-black font-light tracking-wider text-sm transition-all duration-300 border border-[#00FF7F]/20"
              >
                Get Kasango AI Pro →
              </a>
            </div>

            {/* CashBet AI Card - Tesla Glass */}
            <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-700 p-8 hover:shadow-2xl hover:-translate-y-2">
              {/* Animated Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              <div className="relative z-10 mb-6">
                <div className="w-12 h-12 rounded-lg bg-white/10 border border-white/30 flex items-center justify-center mb-6">
                  <div className="w-6 h-6 rounded-full bg-white"></div>
                </div>
                <h3 className="text-2xl font-light text-white mb-4 tracking-wide">CashBet AI</h3>
                <p className="text-gray-400 font-extralight leading-relaxed mb-6 text-sm">
                  AI-powered Aviator predictor for gaming intelligence with advanced pattern recognition.
                </p>
                <div className="space-y-3 mb-8 text-sm text-gray-300">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    <span className="font-extralight">Real-time Predictions</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    <span className="font-extralight">Pattern Analysis</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-white"></div>
                    <span className="font-extralight">Smart Notifications</span>
                  </div>
                </div>
              </div>
              <a
                href="https://whop.com/cashbet-ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="relative z-10 block w-full text-center px-6 py-4 rounded-xl backdrop-blur-md bg-white/90 hover:bg-white text-black font-light tracking-wider text-sm transition-all duration-300 border border-white/20"
              >
                Get CashBet AI →
              </a>
            </div>

            {/* Ghatti Academy Card - Tesla Glass */}
            <div className="group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-blue-500/50 transition-all duration-700 p-8 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2">
              {/* Animated Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              {/* Badge */}
              <div className="absolute top-4 right-4 px-3 py-1 rounded-full backdrop-blur-md bg-blue-500/20 border border-blue-500/50 text-blue-400 text-xs font-light tracking-wider">
                FREE
              </div>
              
              <div className="relative z-10 mb-6">
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6">
                  <div className="w-6 h-6 rounded bg-blue-500"></div>
                </div>
                <h3 className="text-2xl font-light text-white mb-4 tracking-wide">Ghatti Academy</h3>
                <p className="text-gray-400 font-extralight leading-relaxed mb-6 text-sm">
                  Free lessons on Forex patterns, AI setups, and automation strategies from industry experts.
                </p>
                <div className="space-y-3 mb-8 text-sm text-gray-300">
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-blue-500"></div>
                    <span className="font-extralight">Video Tutorials</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-blue-500"></div>
                    <span className="font-extralight">Trading Strategies</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full bg-blue-500"></div>
                    <span className="font-extralight">AI Setup Guides</span>
                  </div>
                </div>
              </div>
              <a
                href="https://t.me/winnersgroundPwR1"
                target="_blank"
                rel="noopener noreferrer"
                className="relative z-10 block w-full text-center px-6 py-4 rounded-xl backdrop-blur-md bg-blue-500/90 hover:bg-blue-500 text-white font-light tracking-wider text-sm transition-all duration-300 border border-blue-500/20"
              >
                Start Learning →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE PRICES WIDGET */}
      <LivePricesWidget />

      {/* EXNESS BROKER ADVERTISEMENT */}
      <ExnessBrokerAd />

      {/* MAGAZINE LAYOUT - Sidebar + Main Content */}
      <div className="flex bg-black">
        {/* Trending Sidebar - Fixed (Desktop only) */}
        <TrendingSidebar />

        {/* Main Content Column */}
        <main className="flex-1 bg-black">
        
        {/* MOBILE TRENDING SECTION - Shows only on mobile */}
        <MobileTrendingSection />
        
        {/* FEATURED ARTICLES SECTION - FORBES STYLE (MOVED TO TOP) */}
        <FeaturedArticlesSection articles={articles} />

        {/* YOUTUBE VIDEOS SECTION */}
        <YouTubeSection />

        {/* PRODUCTS SECTION - SHOP */}
        <section className="py-24 px-6 max-w-7xl mx-auto border-t border-zinc-800">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-extralight tracking-tight mb-4">
              GET STARTED TODAY
            </h2>
            <p className="text-gray-400 font-light text-lg">Choose your trading solution and start profiting</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Kasango AI Pro Card */}
            <article className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-zinc-800 hover:border-white/50 transition-all duration-500 overflow-hidden group">
              <div className="p-10">
                {/* Badge */}
                <div className="inline-block px-4 py-1 bg-white text-black text-xs font-bold tracking-widest mb-6">
                  BEST SELLER
                </div>
                
                {/* Product Name */}
                <h3 className="text-4xl font-light mb-2 group-hover:text-white transition-colors">
                  Kasango AI Pro
                </h3>
                <p className="text-sm text-gray-500 mb-4 italic">Powered by Kolt AI Engine</p>
                
                {/* Description */}
                <p className="text-gray-400 font-light leading-relaxed mb-6 text-lg">
                  Advanced AI-powered forex trading software with rule-based execution. Automated trading assistance, risk management, and multi-currency support. Join 1,250+ traders worldwide.
                </p>
                
                {/* Features */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">AI-Powered Trading Algorithm</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">24/7 Automated Trading</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Risk Management Tools</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Multi-Currency Support</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Lifetime Updates & Support</span>
                  </li>
                </ul>
                
                {/* CTA Buttons */}
                <div className="space-y-3">
                  <button 
                    onClick={() => {
                      addToCart(products.kasango);
                      alert('Kasango AI Pro added to cart!');
                    }}
                    className="block w-full py-4 bg-white text-black text-center text-sm font-bold tracking-widest hover:bg-gray-200 transition-all duration-300"
                  >
                    ADD TO CART
                  </button>
                  <a 
                    href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 border border-zinc-700 text-center text-sm font-light tracking-widest hover:bg-zinc-800 transition-all duration-300"
                  >
                    BUY NOW →
                  </a>
                </div>
              </div>
            </article>

            {/* CashBet AI Card */}
            <article className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-zinc-800 hover:border-white/50 transition-all duration-500 overflow-hidden group">
              <div className="p-10">
                {/* Badge */}
                <div className="inline-block px-4 py-1 bg-zinc-700 text-white text-xs font-bold tracking-widest mb-6">
                  GAMING AI
                </div>
                
                {/* Product Name */}
                <h3 className="text-4xl font-light mb-4 group-hover:text-white transition-colors">
                  CashBet AI
                </h3>
                
                {/* Description */}
                <p className="text-gray-400 font-light leading-relaxed mb-6 text-lg">
                  Next-generation Aviator predictor and gaming intelligence tool. Smart predictions powered by AI to help you make informed betting decisions with confidence.
                </p>
                
                {/* Features */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">AI-Powered Predictions</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Real-Time Analysis</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Smart Betting Strategies</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Mobile-Friendly Interface</span>
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-light">Regular Updates</span>
                  </li>
                </ul>
                
                {/* CTA Buttons */}
                <div className="space-y-3">
                  <button 
                    onClick={() => {
                      addToCart(products.cashbet);
                      alert('CashBet AI added to cart!');
                    }}
                    className="block w-full py-4 bg-white text-black text-center text-sm font-bold tracking-widest hover:bg-gray-200 transition-all duration-300"
                  >
                    ADD TO CART
                  </button>
                  <a 
                    href="https://whop.com/cashbet-ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 border border-zinc-700 text-center text-sm font-light tracking-widest hover:bg-zinc-800 transition-all duration-300"
                  >
                    BUY NOW →
                  </a>
                </div>
              </div>
            </article>
          </div>

          {/* Money Back Guarantee */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 border border-zinc-700 bg-zinc-900">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-gray-300 font-light">Secure Checkout via Whop • Trusted by Thousands</span>
            </div>
          </div>
        </section>

        {/* FREE TRADING GAME SECTION */}
        <section className="py-24 px-6 max-w-7xl mx-auto border-t border-zinc-800">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Game Info */}
            <div>
              <div className="inline-block px-4 py-1 bg-green-500/20 border border-green-500 text-green-500 text-xs font-bold tracking-widest mb-6">
                100% FREE TO PLAY
              </div>
              <h2 className="text-5xl md:text-6xl font-extralight tracking-tight mb-6">
                LEARN FOREX
                <br />
                <span className="font-light">WHILE YOU PLAY</span>
              </h2>
              <p className="text-gray-400 font-light leading-relaxed text-lg mb-8">
                Master trading patterns through our addictive "Buy or Sell Board" game. Learn to read candlesticks, identify trends, and make instant trading decisions — just like the pros.
              </p>

              {/* Features */}
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white text-black flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                  <div>
                    <div className="font-light mb-1">Real Trading Patterns</div>
                    <div className="text-sm text-gray-500">Learn from actual market scenarios</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white text-black flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                  <div>
                    <div className="font-light mb-1">Instant Feedback</div>
                    <div className="text-sm text-gray-500">Learn what works and why</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-white text-black flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                  <div>
                    <div className="font-light mb-1">Level Up Your Skills</div>
                    <div className="text-sm text-gray-500">Progress from Beginner to Master Trader</div>
                  </div>
                </li>
              </ul>

              <Link
                to="/game"
                className="inline-block px-12 py-4 bg-white text-black text-sm font-bold tracking-widest hover:bg-gray-200 transition-colors"
              >
                PLAY NOW - FREE →
              </Link>
            </div>

            {/* Right: Game Preview */}
            <div className="relative">
              <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-zinc-800 p-8">
                {/* Mini Game Board */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { emoji: '📈', color: 'border-green-500 bg-green-500/10' },
                    { emoji: '📉', color: 'border-red-500 bg-red-500/10' },
                    { emoji: '⬆️', color: 'border-green-500 bg-green-500/10' },
                    { emoji: '🔨', color: 'border-zinc-800' },
                    { emoji: '⭐', color: 'border-zinc-800' },
                    { emoji: '🚀', color: 'border-zinc-800' },
                    { emoji: '⬇️', color: 'border-red-500 bg-red-500/10' },
                    { emoji: '📊', color: 'border-zinc-800' },
                    { emoji: '⚠️', color: 'border-red-500 bg-red-500/10' },
                  ].map((cell, idx) => (
                    <div
                      key={idx}
                      className={`aspect-square border-2 ${cell.color} flex items-center justify-center text-3xl`}
                    >
                      {cell.emoji}
                    </div>
                  ))}
                </div>

                {/* Score Display */}
                <div className="flex justify-around text-center mb-6 border-t border-zinc-800 pt-6">
                  <div>
                    <div className="text-3xl font-light">+4</div>
                    <div className="text-xs text-gray-500">SCORE</div>
                  </div>
                  <div>
                    <div className="text-3xl font-light">3🔥</div>
                    <div className="text-xs text-gray-500">STREAK</div>
                  </div>
                  <div>
                    <div className="text-2xl font-light text-blue-500">Analyst</div>
                    <div className="text-xs text-gray-500">LEVEL</div>
                  </div>
                </div>

                {/* Action Buttons Preview */}
                <div className="flex gap-3">
                  <div className="flex-1 py-3 bg-green-600 text-center text-sm font-bold">
                    🟢 BUY
                  </div>
                  <div className="flex-1 py-3 bg-red-600 text-center text-sm font-bold">
                    🔴 SELL
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -top-4 -right-4 bg-yellow-500 text-black px-6 py-3 font-bold text-sm shadow-lg">
                5 SEC TIMER!
              </div>
            </div>
          </div>
        </section>

        {/* JOHN GHATTI'S STORY SECTION */}
        <section className="py-32 px-6 max-w-7xl mx-auto border-t border-zinc-800">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Story Content */}
            <div>
              <div className="text-xs tracking-widest text-gray-500 mb-4">THE STORY</div>
              <h2 className="text-5xl md:text-6xl font-extralight tracking-tight mb-8">
                FROM FRUSTRATED
                <br />
                <span className="font-light">TRADER TO INNOVATOR</span>
              </h2>
              
              <div className="space-y-6 text-gray-400 font-light leading-relaxed">
                <p>
                  John Ghatti's journey began where many traders find themselves — trapped in the cycle of manual trading, 
                  battling fatigue, fear, and emotional decision-making that consistently undermined profitability.
                </p>
                
                <p>
                  After years of mastering trading strategies and testing algorithmic models, John made a decision that would 
                  change everything: <span className="text-white font-medium">build a robot that could trade without the human flaws</span> that had 
                  plagued his success. The first version of Kasango 1.0 was born — tested privately, refined relentlessly, 
                  and generating consistent profits over time.
                </p>
                
                <p>
                  But John didn't stop at personal success. Seeing the potential to democratize automated trading, he launched 
                  a <span className="text-white font-medium">franchise system</span> that allowed skilled traders across Africa and Asia to build their own 
                  brands powered by Kasango AI. What started as a personal solution became a movement.
                </p>
                
                <p>
                  Today, John Ghatti operates multiple ventures — from <span className="text-white font-medium">Kasango AI</span> and <span className="text-white font-medium">CashBet AI</span> to 
                  <span className="text-white font-medium"> Ghatti Trading</span> and <span className="text-white font-medium">Go Down Tea</span> — all united by a common mission: 
                  <span className="text-white font-medium"> empower individuals through technology, mentorship, and results-driven innovation</span>.
                </p>
              </div>
            </div>

            {/* Right: Key Milestones */}
            <div className="space-y-8">
              <div className="bg-zinc-900 border border-zinc-800 p-8">
                <h3 className="text-2xl font-light mb-8">KEY MILESTONES</h3>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 border-2 border-white flex items-center justify-center">
                      <span className="text-2xl font-light">01</span>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">The Struggle</h4>
                      <p className="text-sm text-gray-400 font-light">Years of inconsistent manual trading and emotional mistakes led to frustration and determination for change.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 border-2 border-white flex items-center justify-center">
                      <span className="text-2xl font-light">02</span>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">The Breakthrough</h4>
                      <p className="text-sm text-gray-400 font-light">Kasango 1.0 developed and tested privately, generating reliable profits through algorithmic precision.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 border-2 border-white flex items-center justify-center">
                      <span className="text-2xl font-light">03</span>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">The Movement</h4>
                      <p className="text-sm text-gray-400 font-light">Franchise model launched, empowering traders across 30+ countries to build their own AI-powered brands.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 border-2 border-white flex items-center justify-center">
                      <span className="text-2xl font-light">04</span>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">The Expansion</h4>
                      <p className="text-sm text-gray-400 font-light">Multiple ventures launched: CashBet AI, Ghatti Trading, Go Down Tea — all focused on innovation and results.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-l-4 border-white pl-6">
                <blockquote className="text-xl font-light italic text-gray-300">
                  "Start with a problem you understand deeply. The best businesses come from personal frustration — 
                  solve your own need first."
                </blockquote>
                <p className="text-sm text-gray-500 mt-4">— JOHN GHATTI</p>
              </div>
            </div>
          </div>
        </section>

        {/* COMPANIES & SUBSCRIBE SECTION */}
        <section id="about" className="py-24 px-6 max-w-7xl mx-auto border-t border-zinc-800">
          {/* Featured Company Showcase */}
          <div className="mb-16 p-12 bg-zinc-900 border border-zinc-800">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extralight tracking-tight mb-2">FLAGSHIP COMPANY</h2>
              <p className="text-gray-400 font-light">Pioneering AI-Powered Forex Trading in Africa</p>
            </div>
            <div className="flex flex-col items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/iyucmfc7_image.png"
                alt="Kasango AI Pro"
                className="h-24 mb-6 object-contain"
                style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }}
              />
              <p className="text-gray-400 font-light text-center max-w-3xl leading-relaxed mb-6">
                AI-powered Forex trading ecosystem with franchise model operating across 30+ countries. 
                Kasango AI Pro (powered by Kolt AI Engine) democratizes automated trading with cutting-edge algorithms and real-time market analysis.
              </p>
              <a 
                href="https://app.winnersgroundfx.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-3 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
              >
                VISIT KASANGO AI
                <span>→</span>
              </a>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* About & Companies */}
            <div className="space-y-8">
              <div className="bg-zinc-900 border border-zinc-800 p-8" data-testid="about-section">
                <h3 className="text-3xl font-extralight tracking-tight mb-6">ABOUT</h3>
                <p className="text-gray-400 font-light leading-relaxed">
                  Multi-industry entrepreneur, forex trading mogul, and founder of Kasango 1.0 — an AI-powered trading robot that automates and multiplies profits in forex markets. 
                  With operations across Africa and Asia, John transforms how everyday people approach finance, investing, and trading. 
                  He also founded CashBet (AI betting predictor), Ghatti Trading (AI development & automation company), and Go Down Tea (premium health brand). 
                  His reputation is grounded in results, mentorship, and innovation, shaping a new class of African tech leaders.
                </p>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 p-8" data-testid="companies-section">
                <h3 className="text-3xl font-extralight tracking-tight mb-6">COMPANIES</h3>
                <div className="space-y-6">
                  <div className="border-l-2 border-white pl-6">
                    <div className="flex items-center gap-4 mb-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/iyucmfc7_image.png"
                        alt="Kasango AI Pro"
                        className="h-12 object-contain"
                        style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }}
                      />
                    </div>
                    <h4 className="text-lg font-medium mb-2">KASANGO AI PRO</h4>
                    <p className="text-sm text-gray-400 font-light">Powered by Kolt AI Engine • Franchise model across 30+ countries</p>
                  </div>
                  <div className="border-l-2 border-white/30 pl-6">
                    <div className="flex items-center gap-4 mb-3">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_b9ac4c36-714b-45c0-9160-f047c039178c/artifacts/dr2tyzs4_image.png"
                        alt="CashBet AI"
                        className="h-12 object-contain"
                        style={{ mixBlendMode: 'screen', filter: 'brightness(1.2)' }}
                      />
                    </div>
                    <h4 className="text-lg font-medium mb-2">CASHBET AI</h4>
                    <p className="text-sm text-gray-400 font-light">Predictive intelligence tool for smart betting decisions</p>
                  </div>
                  <div className="border-l-2 border-white/30 pl-6">
                    <h4 className="text-lg font-medium mb-2">GHATTI TRADING</h4>
                    <p className="text-sm text-gray-400 font-light">Forward-thinking technology company specializing in AI development and intelligent automation tools</p>
                  </div>
                  <div className="border-l-2 border-white/30 pl-6">
                    <h4 className="text-lg font-medium mb-2">GO DOWN TEA</h4>
                    <p className="text-sm text-gray-400 font-light">Premium health brand for elite male performance</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscribe */}
            <div className="flex items-center">
              <div className="bg-white text-black p-12 w-full" data-testid="subscribe-section">
                <h3 className="text-4xl font-extralight tracking-tight mb-4">STAY UPDATED</h3>
                <p className="text-gray-700 font-light mb-8">Get exclusive insights on AI-powered trading, expert advisors, and forex strategies.</p>
                <form className="space-y-4" onSubmit={handleSubscribe}>
                  <input
                    type="email"
                    placeholder="YOUR EMAIL"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full border-b-2 border-black bg-transparent px-0 py-3 text-sm font-light tracking-wider focus:outline-none placeholder-gray-500"
                    data-testid="subscribe-email-input"
                  />
                  <button 
                    type="submit"
                    className="w-full py-4 bg-black text-white text-sm font-medium tracking-widest hover:bg-gray-800 transition-colors duration-300"
                    data-testid="subscribe-button"
                  >
                    SUBSCRIBE
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="py-16 px-6 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Contact */}
              <div>
                <h4 className="text-sm font-medium tracking-widest mb-4">CONTACT</h4>
                <div className="space-y-2 text-sm text-gray-400 font-light">
                  <div>WhatsApp: <a href="https://wa.me/27685358462" className="hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">+27 75 099 6682</a></div>
                  <div>Email: <a href="mailto:Winnersgroundfx@gmail.com" className="hover:text-white transition-colors">Winnersgroundfx@gmail.com</a></div>
                </div>
              </div>
              
              {/* Social */}
              <div>
                <h4 className="text-sm font-medium tracking-widest mb-4">SOCIAL</h4>
                <div className="space-y-2 text-sm text-gray-400 font-light">
                  <div><a href="https://www.instagram.com/john_ghatti__ceo_" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Instagram</a></div>
                  <div><a href="https://youtube.com/@johnghattiworldwide" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">YouTube</a></div>
                  <div><a href="https://t.me/winnersgroundPwR1" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Telegram</a></div>
                </div>
              </div>
              
              {/* Companies */}
              <div>
                <h4 className="text-sm font-medium tracking-widest mb-4">COMPANIES</h4>
                <div className="space-y-2 text-sm text-gray-400 font-light">
                  <div><a href="https://app.winnersgroundfx.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Kasango AI</a></div>
                  <div><a href="https://cashbetai.co.za" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">CashBet AI</a></div>
                  <div><a href="https://ghattitrading.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ghatti Trading</a></div>
                  <div><a href="#" className="hover:text-white transition-colors">Go Down Tea</a></div>
                </div>
              </div>
            </div>
            
            {/* Trading Disclaimer */}
            <div className="border-t border-zinc-800 pt-8 mt-8">
              <div className="bg-zinc-950 border border-zinc-800 p-6 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="text-yellow-500 font-medium text-sm mb-2">Risk Disclaimer</div>
                    <p className="text-gray-400 text-xs leading-relaxed">
                      <strong className="text-gray-300">Trade responsibly:</strong> Trading CFDs (Contracts for Difference) and forex involves substantial risk and may lead to permanent capital loss. Past performance is not indicative of future results. The high degree of leverage can work against you as well as for you. Before deciding to trade forex or any other financial instrument, you should carefully consider your investment objectives, level of experience, and risk appetite. Only invest money you can afford to lose. Kasango AI Pro is a trading tool and does not guarantee profits. All trading results shown are for educational purposes and may not reflect actual trading conditions. Make sure you understand the risks before trading.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-gray-500 font-light border-t border-zinc-800 pt-8 mt-8">
              <div>© 2025 JOHN GHATTI. ALL RIGHTS RESERVED.</div>
              <div className="flex gap-8">
                <a href="#" className="hover:text-white transition-colors">PRIVACY</a>
                <a href="#" className="hover:text-white transition-colors">TERMS</a>
              </div>
            </div>
          </div>
        </footer>
        </main>
      </div>

      {/* Content Teaser - Floating Popup */}
      <ContentTeaser />
    </div>
  );
}


export default JohnHomepage;
